import { supabase } from './supabase'
import { STORAGE_KEY, SAVE_OWNER_KEY, SYNCED_REV_KEY } from '@/store/storageKeys'
/*
 *  TYPE-ONLY, and it has to stay that way.
 *
 *  `import type` is erased at build time, so this costs nothing at runtime.
 *  A value import from persist.ts would reach store/store through
 *  classGear -> rarityColor, and store/store builds the Redux state from
 *  localStorage the moment it evaluates - from the OLD save, before boot has
 *  written the authoritative one. That is the whole bug this module exists to
 *  fix, so the import style here is load-bearing.
 */
import type { PersistedSlices } from '@/store/persist'

/**
 *  THE STORE-FREE HALF OF CLOUD SAVING.
 *
 *  Everything here can run BEFORE the Redux store exists, which is the whole
 *  reason it is a separate module. `saveSync.ts` imports the store (it toasts),
 *  so it cannot participate in boot — and boot is exactly where the save has to
 *  be decided.
 *
 *  ── WHY THAT MATTERS (2026-10-16) ────────────────────────────────────────
 *  The store is built from localStorage at module-evaluation time. So the old
 *  design had no choice: it mounted the game from the local save, fetched the
 *  cloud afterwards, and when the cloud turned out to be newer it wrote the
 *  blob and called `location.reload()`. That reload was capped at one per tab
 *  session, and an installed PWA resuming from background keeps sessionStorage
 *  — so a phone that had spent its reload sat on the old character forever with
 *  saving paused. Reported twice, as *"my character is 95 and on tablet its 97
 *  and wont update on the phone"*.
 *
 *  Resolve the save before the store is imported and the reload is not needed,
 *  the budget is not needed, and the dead end cannot exist.
 *
 *  ── ORDERING IS THE SERVER'S JOB ─────────────────────────────────────────
 *  Nothing here compares timestamps. `updated_at` was stamped by whichever
 *  device wrote it and `lastSeenAt` likewise, so the old comparison asked two
 *  different wall clocks to agree. `saves.rev` (migration 0136) is assigned by
 *  a database trigger and cannot be set by a client, so "has anyone written
 *  since I last synced?" is two integers from one source.
 *
 *  NOTE this module deliberately holds the ONLY implementation of the local
 *  blob read/write and the revision bookkeeping. A second copy of either is how
 *  the read side and the write side drift apart, which this project has now hit
 *  nine times.
 */

// Re-exported so callers that already import it from here keep working.
export { SAVE_OWNER_KEY }

export interface CloudSave {
  data: Partial<PersistedSlices>
  schemaVersion: number
  /** Server-assigned. Monotonic. The only ordering signal in the system. */
  rev: number
}

/** `null` = we could not ask (offline, error). `0` = asked, account has no save. */
export type CloudRev = number | null

// ── local bookkeeping ───────────────────────────────────────────────────

export function readSyncedRev(): number | null {
  try {
    const raw = window.localStorage.getItem(SYNCED_REV_KEY)
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) && n >= 0 ? n : null
  } catch {
    return null
  }
}

export function writeSyncedRev(rev: number): void {
  try { window.localStorage.setItem(SYNCED_REV_KEY, String(rev)) } catch { /* private mode */ }
}

export function clearSyncedRev(): void {
  try { window.localStorage.removeItem(SYNCED_REV_KEY) } catch { /* ignore */ }
}

export function readLocalBlob(): Record<string, unknown> | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) as Record<string, unknown> : null
  } catch {
    return null
  }
}

/**
 *  Replace the local blob with a cloud save, and record the revision it came
 *  from in the SAME call.
 *
 *  Writing the blob without the revision would leave the device claiming to be
 *  synced at an older revision than the data it now holds, and it would pull
 *  again on every boot forever. They are one operation, so they live in one
 *  function.
 */
export function writeLocalFromCloud(save: CloudSave): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      _schemaVersion: save.schemaVersion,
      ...save.data,
    }))
    writeSyncedRev(save.rev)
  } catch { /* quota — boot continues on whatever is already local */ }
}

// ── the cloud ───────────────────────────────────────────────────────────

/**
 *  What revision is the cloud on?
 *
 *  The cheap half of the boot check: if it matches what this device last
 *  synced, the local save is already authoritative and the blob — which can be
 *  tens of kilobytes — never has to be fetched at all. That is what keeps the
 *  new blocking boot fast in the common case of playing on one device.
 */
export async function fetchCloudRev(): Promise<CloudRev> {
  if (!supabase) return 0
  const { data, error } = await supabase.rpc('save_rev')
  if (error) {
    console.warn('[saveTransport] save_rev failed', error)
    return null
  }
  const n = Number(data)
  return Number.isFinite(n) ? n : null
}

/** Fetch the whole cloud save. Throws on a server error, so a failure can
 *  never be mistaken for an empty account — that confusion is what let a
 *  device claim ownership and push its empty state over a real character. */
export async function fetchCloudSave(userId: string): Promise<CloudSave | null> {
  if (!supabase) return null
  const { data, error } = await supabase
    .from('saves')
    .select('data, schema_version, rev')
    .eq('user_id', userId)
    .maybeSingle()
  if (error) throw new Error(`fetchCloudSave failed: ${error.message ?? 'unknown error'}`)
  if (!data) return null
  return {
    data: data.data as Partial<PersistedSlices>,
    schemaVersion: data.schema_version as number,
    // A row written before 0136 reports 0; treat it as revision 1 so it is
    // never read as "never written".
    rev: Math.max(1, Number((data as { rev?: number }).rev ?? 0)),
  }
}

/** Retry transient failures. A blip on sign-in must not look like an empty
 *  cloud, and must not strand the player on a local save either. */
export async function fetchCloudSaveRetrying(userId: string, attempts = 4): Promise<CloudSave | null> {
  let lastErr: unknown
  for (let i = 0; i < attempts; i++) {
    try {
      return await fetchCloudSave(userId)
    } catch (e) {
      lastErr = e
      if (i < attempts - 1) await new Promise(r => window.setTimeout(r, 500 * 2 ** i))
    }
  }
  throw lastErr
}

export type PushOutcome =
  /** Landed. `rev` is the new current revision. */
  | { status: 'ok'; rev: number }
  /**
   *  ANOTHER DEVICE HOLDS THE PLAY SESSION (0137).
   *
   *  The only outcome that justifies telling the player they are playing
   *  somewhere else, because it is the only one the SERVER asserts.
   */
  | { status: 'taken-over'; rev: number }
  /** Somebody wrote since we synced. `rev` is theirs. Nothing was written. */
  | { status: 'stale'; rev: number }
  /**
   *  We never recorded a revision, so we could not name what we were
   *  replacing.
   *
   *  Says NOTHING about other devices — and reporting it as one is the bug
   *  behind *"its telling me im playing on another device ... im not on the
   *  other device"*. It happens whenever a boot could not reach the server,
   *  which leaves the revision unknown.
   */
  | { status: 'unsynced'; rev: number }
  /** Could not reach the server. Nothing was written; try again later. */
  | { status: 'error'; message: string }
  /** No backend configured — local-only mode. */
  | { status: 'offline' }

/**
 *  Push the save, but only if nobody has written it since we synced.
 *
 *  This is a compare-and-swap against `saves.rev`, so a losing write is
 *  REFUSED by the database rather than merely unlikely. Before 0136 the push
 *  was a plain upsert and last-writer-won, including a device writing state it
 *  had read minutes earlier.
 *
 *  On a conflict the caller must NOT retry with a bumped revision — that would
 *  be the clobber with extra steps. The right response is to stop pushing and
 *  pull on the next boot, which now always succeeds because boot happens before
 *  the store exists.
 */
export async function pushSaveCas(
  state: Partial<PersistedSlices>,
  schemaVersion: number,
  session: string,
): Promise<PushOutcome> {
  if (!supabase) return { status: 'offline' }
  const expected = readSyncedRev()
  const { data, error } = await supabase.rpc('push_save', {
    p_data: state,
    p_schema_version: schemaVersion,
    p_expected_rev: expected,
    // 0137: the server refuses a write from a device that no longer holds the
    // play session. This is also how a taken-over device finds out promptly —
    // on its next autosave, rather than whenever a poll happens to fire.
    p_session: session,
  })
  if (error) {
    console.error('[saveTransport] push_save failed', error)
    return { status: 'error', message: error.message ?? 'unknown error' }
  }
  // `returns table (...)` arrives as an array of one row.
  const row = (Array.isArray(data) ? data[0] : data) as
    { accepted?: boolean; server_rev?: number; reason?: string } | null
  const serverRev = Number(row?.server_rev ?? 0)
  const rev = Number.isFinite(serverRev) ? serverRev : 0

  if (row?.accepted === true) {
    writeSyncedRev(rev)
    return { status: 'ok', rev }
  }

  // Record the winning revision even though we wrote nothing: it is what makes
  // the next boot's comparison correct instead of a guess.
  if (rev > 0) writeSyncedRev(rev)

  /*
   *  TAKE THE SERVER'S WORD FOR WHY.
   *
   *  The first version collapsed every refusal into one 'conflict' and the UI
   *  announced "playing on another device" — which was simply false for the
   *  commonest case, a device that had never recorded a revision. An old
   *  three-argument push (a cached bundle) reports no reason at all; treat that
   *  as 'stale', which is the conservative reading: stop pushing, accuse
   *  nobody.
   */
  switch (row?.reason) {
    case 'session':  return { status: 'taken-over', rev }
    case 'unsynced': return { status: 'unsynced', rev }
    case 'no-row':   return { status: 'unsynced', rev }
    default:         return { status: 'stale', rev }
  }
}
